import cx from "classnames";
import React from "react";
import { FormErrors } from "redux-form";

import { BtnType } from "@/mk2/components/Btn";
import { Form } from "@/mk2/components/Form";
import { SubmitButton } from "@/mk2/components/forms/SubmitButton";
import { TextareaField } from "@/mk2/components/forms/TextareaField";
import { ComposeToolbar, FIXED, ToolbarTheme } from "@/mk2/containers/Toolbar/Toolbar";
import { ToolbarAdditionalCheckbox } from "@/mk2/containers/Toolbar/ToolbarAdditionalCheckbox";
import { MKReduxFormInjectedProps, mkReduxForm } from "@/mk2/decorators/mkReduxForm";
import { Photo } from "@/mk2/decorators/withUploader";
import {
    CONTESTS_ADD_REPLY,
    FORUM_ADD_REPLY,
    FORUM_COMMENT_EDIT_TEXT,
    FORUM_COMMENT_FORM_EMPTY_MESSAGE_CANNOT_BE_SENT,
    SEND_ANONYMOUSLY,
    SEND_AS_ADMIN,
    SEND_COMMENT_FORUM,
} from "@/mk/autogenerated/translations/CommentCreateEditForm.9eb548b3d5f00c9c4e4fa9789aebb050";

import styles from "./CommentCreateEditForm.mscss";

export const COMMENT_CREATE_FORM_NAME = "COMMENT_CREATE_FORM_NAME";
export const COMMENT_EDIT_FORM_NAME = "COMMENT_EDIT_FORM_NAME";

const ALLOW_SEND_ANONYMOUSLY = true;

export interface CommentFormData {
    text: string;
    photos: Photo[];
    sticker: number;
    asAdmin?: boolean;
    asAnonym?: boolean;
    repliedToCommentId?: number;
    score?: boolean;
}

interface OwnProps {
    anonymousMode?: boolean;
    hasBlueToolbar?: boolean;
    isEdit: boolean;
    isContest: boolean;
    isBlogsComment: boolean;
    isGroupsComment: boolean;
    isMobile: boolean;
    isIOS: boolean;
    isUnauthenticatedUser?: boolean;
    initialValues: CommentFormData;
    currentValues?: CommentFormData;
    requestUserPermissions: string[];

    // counselling reply - unauthenticated upload (e.g. accessKey='counselling_reply:slug:pE96mf3AUfB4mLJv')
    accessKey?: string;

    textareaRef?: (r: HTMLTextAreaElement) => void;
    onSave(values: CommentFormData);
}

type Props = OwnProps;

const emojisConfig = {
    fields: ["text"],
    defaultField: "text",
};

const photosConfigInForum = {
    photoType: "forum",
    multiple: true,
    showPhotoCode: false,
};

const photosConfigInBlogsAndGroups = {
    photoType: "comment",
    multiple: false,
    showPhotoCode: false,
};

class CommentCreateEditFormComponent extends React.Component<
    Props & MKReduxFormInjectedProps<CommentFormData, Props>
> {
    public static validate(values: CommentFormData): FormErrors<CommentFormData> {
        const errors: FormErrors<CommentFormData> = {};
        if (!values.text?.trim()?.length && !values.photos?.length && !values.sticker) {
            errors.text = FORUM_COMMENT_FORM_EMPTY_MESSAGE_CANNOT_BE_SENT;
        }

        return errors;
    }

    public render() {
        const {
            accessKey,
            anonymousMode: anonymousModeFromPost,
            isContest,
            isEdit,
            isMobile,
            isIOS,
            isUnauthenticatedUser,
            textareaRef,
            requestUserPermissions,
            isBlogsComment,
            isGroupsComment,
            hasBlueToolbar,
        } = this.props;
        const { currentValues, form, handleSubmit } = this.props;

        const anonymousAuthor = anonymousModeFromPost || currentValues?.asAnonym;

        const isAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;
        const mobileOrIOS = isMobile || isIOS;
        const isCounselling = !!accessKey;
        const isForumComment = !(isBlogsComment || isGroupsComment);

        const photosConfig =
            isBlogsComment || isGroupsComment ? photosConfigInBlogsAndGroups : photosConfigInForum;

        const toolbarTheme = isEdit
            ? ToolbarTheme.White
            : anonymousAuthor
              ? ToolbarTheme.Black
              : isMobile || hasBlueToolbar
                ? ToolbarTheme.Blue
                : ToolbarTheme.White;

        return (
            <Form
                className={cx(
                    styles.CommentCreateEditForm,
                    isEdit && styles["CommentCreateEditForm--edit"],
                    !isEdit && styles["CommentCreateEditForm--create"],
                    (mobileOrIOS || anonymousAuthor || hasBlueToolbar) &&
                        styles["CommentCreateEditForm--mobileOrIOS"],
                )}
                onSubmit={handleSubmit}
            >
                {isEdit ? (
                    [
                        <TextareaField
                            key="text"
                            name="text"
                            rows={isMobile ? 20 : 25}
                            label={FORUM_COMMENT_EDIT_TEXT}
                            bottomBorder={false}
                            fillRemainingSpace
                        />,

                        <ComposeToolbar
                            key="toolbar"
                            formName={form}
                            emojis={emojisConfig}
                            photos={photosConfig}
                            isFixed={FIXED.IF_MOBILE_BUT_IOS}
                            isInForum
                            accessKey={accessKey}
                            rightAction={
                                isCounselling && (
                                    <SubmitButton
                                        className={cx(
                                            styles.CommentCreateEditForm__submit,
                                            mobileOrIOS &&
                                                styles[
                                                    "CommentCreateEditForm--mobileOrIOS__submit"
                                                ],
                                        )}
                                        canBeVisuallyDisabled={false}
                                        label={SEND_COMMENT_FORUM}
                                        formName={form}
                                        btnType={BtnType.BlueText}
                                    />
                                )
                            }
                        />,
                    ]
                ) : (
                    <ComposeToolbar
                        formName={form}
                        emojis={emojisConfig}
                        photos={isUnauthenticatedUser ? undefined : photosConfig}
                        isInForum
                        isInCounsellings={isCounselling}
                        isBlack={anonymousAuthor}
                        isBlue={hasBlueToolbar}
                        isFixed={FIXED.NEVER}
                        accessKey={accessKey}
                        rightAction={
                            <SubmitButton
                                className={cx(
                                    styles.CommentCreateEditForm__submit,
                                    (mobileOrIOS || anonymousAuthor || hasBlueToolbar) &&
                                        styles["CommentCreateEditForm--mobileOrIOS__submit"],
                                    hasBlueToolbar &&
                                        !mobileOrIOS &&
                                        styles["CommentCreateEditForm--blue__submit"],
                                )}
                                canBeVisuallyDisabled={false}
                                label={SEND_COMMENT_FORUM}
                                formName={form}
                                btnType={
                                    anonymousAuthor
                                        ? BtnType.BlackTextWithWhiteBackground
                                        : isMobile || hasBlueToolbar
                                          ? BtnType.White
                                          : BtnType.BlueText
                                }
                            />
                        }
                        textArea={
                            <div
                                className={cx(
                                    (mobileOrIOS || anonymousAuthor || hasBlueToolbar) &&
                                        styles[
                                            "CommentCreateEditForm--mobileOrIOS__textareaWrapper"
                                        ],
                                    hasBlueToolbar &&
                                        !mobileOrIOS &&
                                        styles["CommentCreateEditForm--blue__textareaWrapper"],
                                )}
                            >
                                <div
                                    className={cx(
                                        styles.CommentCreateEditForm__textareaWrapper__lvl2,
                                        hasBlueToolbar &&
                                            !mobileOrIOS &&
                                            styles[
                                                "CommentCreateEditForm--blue__textareaWrapper__lvl2"
                                            ],
                                    )}
                                >
                                    <TextareaField
                                        name="text"
                                        rows={1}
                                        maxRows={
                                            // vidno max 5 riadkov textu, potom sa spravi scrollbar
                                            isMobile ? 5 : 25
                                        }
                                        placeholder={
                                            // kvoli ios, musime vykreslit ako placeholder={} a nie ako hint={},
                                            // aby bol kurzor dobre zaindentovany
                                            isContest ? CONTESTS_ADD_REPLY : FORUM_ADD_REPLY
                                        }
                                        bottomBorder={false}
                                        marginLeft={false}
                                        textareaRef={textareaRef}
                                        isCompact
                                        scrollDownOnGrow
                                    />
                                </div>
                            </div>
                        }
                        additionalActions={
                            <div className={styles.CommentCreateEditForm__additionalCheckboxes}>
                                {isAdmin && (
                                    <ToolbarAdditionalCheckbox
                                        isAdminUI
                                        label={SEND_AS_ADMIN}
                                        name={"asAdmin"}
                                        theme={toolbarTheme}
                                    />
                                )}
                                {isForumComment &&
                                    !isUnauthenticatedUser &&
                                    !anonymousModeFromPost &&
                                    ALLOW_SEND_ANONYMOUSLY &&
                                    !isCounselling &&
                                    !isContest &&
                                    !isBlogsComment &&
                                    !isGroupsComment && (
                                        <ToolbarAdditionalCheckbox
                                            label={SEND_ANONYMOUSLY}
                                            name={"asAnonym"}
                                            theme={toolbarTheme}
                                        />
                                    )}
                            </div>
                        }
                    />
                )}
            </Form>
        );
    }
}

export const CommentCreateEditFormFactory = (
    formName: string,
    photosConfig?: any,
    onChange?: (newValues: CommentFormData, oldValues: CommentFormData) => void,
) => {
    return mkReduxForm<CommentFormData, Props>({
        formName,
        photosConfig: photosConfig ? photosConfig : photosConfigInForum,
        onValidate: (values) => CommentCreateEditFormComponent.validate(values),
        onSave: (values, props) => props.onSave(values),
        onChange,
    })(CommentCreateEditFormComponent);
};

// default (forum) comment create/edit forms with default (forum) photoConfig
export const CommentCreateForm = CommentCreateEditFormFactory(COMMENT_CREATE_FORM_NAME);
export const CommentEditForm = CommentCreateEditFormFactory(COMMENT_EDIT_FORM_NAME);

// blogs and groups comment create/edit forms with special photoConfig
export const BlogCommentEditForm = CommentCreateEditFormFactory(
    COMMENT_EDIT_FORM_NAME,
    photosConfigInBlogsAndGroups,
);
